import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
/**
 * Fenêtre de confirmation du site (remplace window.confirm, la fenêtre grise du navigateur).
 *
 *   if (!(await confirmDialog({ title: "Supprimer « X » ?", description: "…" }))) return;
 *
 * Options : title, description, confirmLabel (« Supprimer »), cancelLabel (« Annuler »),
 * tone : "danger" (rouge, icône corbeille — par défaut) ou "warning" (orange, icône alerte).
 * Renvoie une promesse : true si l'utilisateur confirme, false sinon (Annuler, Échap, clic à côté).
 * <ConfirmHost /> est placé une seule fois dans App.js.
 */
let openHandler = null;
export function confirmDialog(options) {
  if (!openHandler) return Promise.resolve(window.confirm(options?.title || "Confirmer ?"));
  return new Promise((resolve) => openHandler({ ...options, resolve }));
}
export function ConfirmHost() {
  const [req, setReq] = useState(null);
  const okRef = useRef(null);
  useEffect(() => {
    openHandler = (r) => setReq(r);
    return () => {
      openHandler = null;
    };
  }, []);
  useEffect(() => {
    if (!req) return undefined;
    okRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req]);
  const close = (value) => {
    if (!req) return;
    req.resolve(value);
    setReq(null);
  };
  if (!req) return null;
  const warning = req.tone === "warning";
  const Icon = warning ? AlertTriangle : Trash2;
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && close(false)}
      // cliquable même ouverte par-dessus une autre fenêtre (qui désactive les clics sur le reste de la page)
      style={{ pointerEvents: "auto" }}
      data-testid="confirm-dialog"
    >
      <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#121214] p-6 shadow-2xl" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
        <div className="flex gap-3">
          <span className={`grid place-items-center w-10 h-10 rounded-full shrink-0 ${warning ? "bg-amber-500/10 text-amber-300" : "bg-rose-500/10 text-rose-400"}`}>
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <h3 id="confirm-title" className="font-display font-bold text-lg leading-snug">{req.title}</h3>
            {req.description && <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">{req.description}</p>}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={() => close(false)} className="h-9 px-4 rounded-md border border-white/10 text-sm hover:bg-white/5" data-testid="confirm-cancel">
            {req.cancelLabel || "Annuler"}
          </button>
          <button
            type="button"
            ref={okRef}
            onClick={() => close(true)}
            className={`h-9 px-4 rounded-md text-sm font-medium ${warning ? "bg-amber-400 text-black hover:bg-amber-300" : "bg-rose-500 text-white hover:bg-rose-400"}`}
            data-testid="confirm-ok"
          >
            {req.confirmLabel || "Supprimer"}
          </button>
        </div>
      </div>
    </div>
  );
}
